// "Add a note from experience" — the chat entry point for first-hand
// knowledge (Phase 5: "share by default, private as option").
//
// Every note is saved IMMEDIATELY to the author's private notebook
// (api/contributions.js embeds it as a private user_note chunk). Shared
// notes (the default) additionally queue for admin review; approval earns
// +20 messages (api/admin2.js).
//
// The parent can force the form open (e.g. the out-of-messages box's
// [Add a note] button) via the `open`/`onOpenChange` props.
import { useState } from 'react';
import { submitContribution } from './feedbackApi';

// Legacy key from before the rebrand — kept so users who already
// accepted the terms don't get asked again (renaming it would reset them).
const TOS_KEY = 'shipassist_contribution_tos_accepted';
const TOS_TEXT = 'This is my own knowledge/experience, not copied from copyrighted material.';
const HINT =
  'Good notes: symptom → likely cause → what to check → the fix. Own experience only — copied manual text is rejected.';

function tosAccepted() {
  try {
    return window.localStorage.getItem(TOS_KEY) === 'yes';
  } catch {
    return false;
  }
}

function rememberTos() {
  try {
    window.localStorage.setItem(TOS_KEY, 'yes');
  } catch {
    // private mode etc. — the checkbox will simply show again next time
  }
}

const radioLabelStyle = checked => ({
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
  fontSize: 11.5,
  lineHeight: 1.5,
  color: checked ? 'var(--text)' : 'var(--text-2)',
  cursor: 'pointer',
});

export default function ContributionNote({ equipmentPath, open, onOpenChange }) {
  // Uncontrolled fallback so the component keeps working standalone.
  const [selfOpen, setSelfOpen] = useState(false);
  const isOpen = open !== undefined ? open : selfOpen;
  const setOpen = next => {
    if (onOpenChange) onOpenChange(next);
    else setSelfOpen(next);
  };

  const [content, setContent] = useState('');
  const [share, setShare] = useState(true); // share by default
  const [tosChecked, setTosChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // {ok, text}
  const needsTos = !tosAccepted();

  async function submit(e) {
    e.preventDefault();
    const text = content.trim();
    if (text.length < 10 || busy || (needsTos && !tosChecked)) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await submitContribution({ equipmentPath, content: text, share });
      rememberTos();
      setMessage({ ok: true, text: result.message });
      setContent('');
      setOpen(false);
    } catch (err) {
      setMessage({ ok: false, text: err.message || 'Could not submit the note.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-3)' }}>
      {!isOpen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              setMessage(null);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-3)',
              fontSize: 11.5,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 0,
            }}
          >
            ✍️ Add a note from experience
          </button>
          {message && (
            <span style={{ color: message.ok ? 'var(--green)' : 'var(--red)' }}>
              {message.text}
            </span>
          )}
        </div>
      )}

      {isOpen && (
        <form
          onSubmit={submit}
          style={{
            background: 'var(--surface-3, #f1f3f9)',
            border: '1px solid var(--border, #e3e7f0)',
            borderRadius: 14,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>
            ✍️ Add a note from experience
          </div>
          <div style={{ fontSize: 11.5, lineHeight: 1.5 }}>{HINT}</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: 0.5 }}>
            EQUIPMENT: {equipmentPath}
          </div>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="e.g. On this purifier model, always check the gravity disc size first when…"
            maxLength={4000}
            rows={4}
            aria-label="Your note"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'var(--surface-1, #ffffff)',
              border: '1px solid var(--border, #e3e7f0)',
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 16,
              fontFamily: 'var(--sans)',
              color: 'var(--text)',
              lineHeight: 1.6,
              resize: 'vertical',
              outline: 'none',
            }}
          />

          {/* Share by default; private is the discreet second option. */}
          <label style={radioLabelStyle(share)}>
            <input
              type="radio"
              name="note-visibility"
              checked={share}
              onChange={() => setShare(true)}
              style={{ marginTop: 2 }}
            />
            <span>
              <strong>Share with the community</strong> — earns +20 messages if approved
            </span>
          </label>
          <label style={{ ...radioLabelStyle(!share), fontSize: 11 }}>
            <input
              type="radio"
              name="note-visibility"
              checked={!share}
              onChange={() => setShare(false)}
              style={{ marginTop: 2 }}
            />
            <span>Keep private (my notebook only)</span>
          </label>

          {needsTos && (
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                fontSize: 11.5,
                lineHeight: 1.5,
                color: 'var(--text-2)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={tosChecked}
                onChange={e => setTosChecked(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>{TOS_TEXT}</span>
            </label>
          )}
          {message && !message.ok && <div style={{ color: 'var(--red)' }}>{message.text}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="submit"
              className="btn"
              disabled={busy || content.trim().length < 10 || (needsTos && !tosChecked)}
              style={{
                padding: '8px 18px',
                fontSize: 12,
                background: 'var(--primary, #16192b)',
                color: '#ffffff',
              }}
            >
              {busy ? 'Saving…' : share ? 'Share note' : 'Save privately'}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setOpen(false)}
              style={{ padding: '8px 14px', fontSize: 12 }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
